import { ClockCircleOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Collapse, DatePicker, Form, Input, Layout, message, Modal, Popconfirm, Space, Tag, TimePicker, Typography } from 'antd';
import dayjs from 'dayjs';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useAuth } from '../../context/DataContext';
import { db } from '../../firebase';
import { uploadFullSeatLayout } from './SeatEvent';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Panel } = Collapse;

const PerformerSessionsSettings = () => {
    const { currentEventId } = useAuth();
    const [loading, setLoading] = useState(true);
    const [venues, setVenues] = useState([]);
    const [sessionsSeatsGenerated, setSessionsSeatsGenerated] = useState({});

    // Modals state
    const [isDateModalVisible, setIsDateModalVisible] = useState(false);
    const [isTimeModalVisible, setIsTimeModalVisible] = useState(false);

    // Selection state
    const [selectedVenueId, setSelectedVenueId] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);

    const [dateForm] = Form.useForm();
    const [timeForm] = Form.useForm();

    useEffect(() => {
        if (currentEventId) {
            fetchEventData();
        }
    }, [currentEventId]);

    const fetchEventData = async () => {
        setLoading(true);
        try {
            const docRef = doc(db, 'events', currentEventId || 'APCS2026');
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                setVenues(data.venues || []);
                setSessionsSeatsGenerated(data.sessionsSeatsGenerated || {});
            }
        } catch (error) {
            console.error("Error fetching event data:", error);
            message.error("Failed to fetch performer sessions data");
        } finally {
            setLoading(false);
        }
    };

    const saveVenuesData = async (newVenues) => {
        try {
            const docRef = doc(db, 'events', currentEventId || 'APCS2026');
            await updateDoc(docRef, { venues: newVenues });
            setVenues(newVenues);
            message.success("Sessions updated successfully");
        } catch (error) {
            console.error("Error saving venues:", error);
            message.error("Failed to save sessions");
        }
    };

    // --- Dates ---
    const handleAddDate = (venueId) => {
        setSelectedVenueId(venueId);
        dateForm.resetFields();
        setIsDateModalVisible(true);
    };

    const onDateSubmit = async () => {
        try {
            const values = await dateForm.validateFields();
            const dateStr = values.date.format('YYYY-MM-DD');
            
            const newVenues = venues.map(v => {
                if (v.id === selectedVenueId) {
                    const venueSessions = v.sessions || {};
                    if (venueSessions[dateStr]) {
                        message.error("This date already exists for this venue!");
                        return v;
                    }
                    return {
                        ...v,
                        sessions: {
                            ...venueSessions,
                            [dateStr]: []
                        }
                    };
                }
                return v;
            });

            // Check if we actually made a change (didn't return early due to error)
            const targetVenue = newVenues.find(v => v.id === selectedVenueId);
            if (targetVenue.sessions[dateStr]) {
                await saveVenuesData(newVenues);
                setIsDateModalVisible(false);
            }
        } catch (err) {
            console.log("Validation failed", err);
        }
    };

    const deleteDate = async (venueId, dateStr) => {
        const newVenues = venues.map(v => {
            if (v.id === venueId) {
                const newSessions = { ...(v.sessions || {}) };
                delete newSessions[dateStr];
                return { ...v, sessions: newSessions };
            }
            return v;
        });
        await saveVenuesData(newVenues);
    };

    // --- Timeslots ---
    const handleAddTime = (venueId, dateStr) => {
        setSelectedVenueId(venueId);
        setSelectedDate(dateStr);
        timeForm.resetFields();
        setIsTimeModalVisible(true);
    };

    const onTimeSubmit = async () => {
        try {
            const values = await timeForm.validateFields();
            const [start, end] = values.timeRange;
            const timeStr = `${start.format('HH:mm')}-${end.format('HH:mm')}`;
            
            const newVenues = venues.map(v => {
                if (v.id === selectedVenueId) {
                    const venueSessions = v.sessions || {};
                    const timeslots = [...(venueSessions[selectedDate] || [])];

                    if (timeslots.includes(timeStr)) {
                        message.error("This timeslot already exists!");
                        return v;
                    }

                    timeslots.push(timeStr);
                    timeslots.sort();

                    return {
                        ...v,
                        sessions: {
                            ...venueSessions,
                            [selectedDate]: timeslots
                        }
                    };
                }
                return v;
            });

            const targetVenue = newVenues.find(v => v.id === selectedVenueId);
            if (targetVenue.sessions[selectedDate].includes(timeStr)) {
                await saveVenuesData(newVenues);
                setIsTimeModalVisible(false);
            }
        } catch (err) {
            console.log("Validation failed", err);
        }
    };

    const deleteTime = async (venueId, dateStr, timeStr) => {
        const newVenues = venues.map(v => {
            if (v.id === venueId) {
                const venueSessions = v.sessions || {};
                const timeslots = (venueSessions[dateStr] || []).filter(t => t !== timeStr);
                return {
                    ...v,
                    sessions: {
                        ...venueSessions,
                        [dateStr]: timeslots
                    }
                };
            }
            return v;
        });
        await saveVenuesData(newVenues);
    };

    const handleGenerateSeats = async (venueId, dateStr) => {
        const targetVenue = venues.find(v => v.id === venueId);
        const timeslots = targetVenue?.sessions?.[dateStr] || [];
        if (timeslots.length === 0) {
            message.warning("No timeslots found for this date. Please add timeslots first.");
            return;
        }

        const sessionsArray = timeslots.map(timeStr => `${dateStr}_${timeStr}`);
        
        try {
            setLoading(true);
            await uploadFullSeatLayout(currentEventId || 'APCS2026', venueId, sessionsArray);
            message.success(`Seats generated successfully for ${sessionsArray.length} sessions.`);
            // Refresh to get updated generation status
            await fetchEventData();
        } catch (error) {
            console.error("Error generating seats:", error);
            message.error("Failed to generate seats. See console for details.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff', borderRadius: 8 }}>
            <div style={{ marginBottom: 24 }}>
                <Title level={3} style={{ margin: 0 }}>Performer Session Setup</Title>
                <Text type="secondary">
                    Manage the schedule of dates and timeslots for performer session assignments. 
                    Venues are managed in the <strong>Venue Settings</strong> page.
                </Text>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>Loading...</div>
            ) : venues.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#888' }}>
                    No venues found. Please create venues in "Venue Settings" first.
                </div>
            ) : (
                <Collapse defaultActiveKey={venues.map(v => v.id)}>
                    {venues.map((venue) => (
                        <Panel
                            key={venue.id}
                            header={
                                <Space>
                                    <Text strong style={{ fontSize: '16px' }}>{venue.label}</Text>
                                    <Text type="secondary">({venue.id})</Text>
                                </Space>
                            }
                        >
                            <div style={{ marginBottom: 16 }}>
                                <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => handleAddDate(venue.id)}>
                                    Add Date
                                </Button>
                            </div>

                            {Object.keys(venue.sessions || {}).sort().map(dateStr => (
                                <div key={dateStr} style={{ marginBottom: 24, padding: '12px 16px', background: '#f5f5f5', borderRadius: 8 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                        <Text strong>{dayjs(dateStr).format('dddd, DD MMM YYYY')}</Text>
                                        <Space>
                                            <Button size="small" type={(venue.sessions[dateStr] || []).length > 0 && (venue.sessions[dateStr] || []).every(t => sessionsSeatsGenerated[`${dateStr}_${t}`]) ? "default" : "primary"} onClick={() => handleGenerateSeats(venue.id, dateStr)}>
                                                {(venue.sessions[dateStr] || []).length > 0 && (venue.sessions[dateStr] || []).every(t => sessionsSeatsGenerated[`${dateStr}_${t}`]) ? "Regenerate Seats" : "Generate Seats"}
                                            </Button>
                                            <Button size="small" type="primary" ghost icon={<PlusOutlined />} onClick={() => handleAddTime(venue.id, dateStr)}>
                                                Add Timeslot
                                            </Button>
                                            <Popconfirm
                                                title="Delete Date"
                                                description="Are you sure you want to delete this date and ALL its timeslots?"
                                                onConfirm={() => deleteDate(venue.id, dateStr)}
                                                okText="Yes"
                                                cancelText="No"
                                            >
                                                <Button size="small" danger icon={<DeleteOutlined />} />
                                            </Popconfirm>
                                        </Space>
                                    </div>

                                    <div>
                                        {!venue.sessions[dateStr] || venue.sessions[dateStr].length === 0 ? (
                                            <Text type="secondary" italic>No timeslots defined</Text>
                                        ) : (
                                            <Space size={[8, 8]} wrap>
                                                {venue.sessions[dateStr].map(timeStr => (
                                                    <Tag
                                                        key={timeStr}
                                                        closable
                                                        onClose={(e) => {
                                                            e.preventDefault();
                                                            deleteTime(venue.id, dateStr, timeStr);
                                                        }}
                                                        icon={<ClockCircleOutlined />}
                                                        style={{ padding: '4px 8px', fontSize: '14px' }}
                                                        color={sessionsSeatsGenerated[`${dateStr}_${timeStr}`] ? "green" : "blue"}
                                                    >
                                                        {timeStr} {sessionsSeatsGenerated[`${dateStr}_${timeStr}`] && "✓ Generated"}
                                                    </Tag>
                                                ))}
                                            </Space>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </Panel>
                    ))}
                </Collapse>
            )}

            {/* Date Modal */}
            <Modal
                title={`Add Date for ${venues.find(v => v.id === selectedVenueId)?.label || selectedVenueId}`}
                open={isDateModalVisible}
                onOk={onDateSubmit}
                onCancel={() => setIsDateModalVisible(false)}
            >
                <Form form={dateForm} layout="vertical">
                    <Form.Item
                        name="date"
                        label="Performance Date"
                        rules={[{ required: true, message: 'Please select a date' }]}
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Time Modal */}
            <Modal
                title={`Add Timeslot for ${venues.find(v => v.id === selectedVenueId)?.label || selectedVenueId} on ${selectedDate}`}
                open={isTimeModalVisible}
                onOk={onTimeSubmit}
                onCancel={() => setIsTimeModalVisible(false)}
            >
                <Form form={timeForm} layout="vertical">
                    <Form.Item
                        name="timeRange"
                        label="Time Range (Start - End)"
                        rules={[{ required: true, message: 'Please select a time range' }]}
                    >
                        <TimePicker.RangePicker 
                            format="HH:mm" 
                            style={{ width: '100%' }} 
                            minuteStep={5}
                            placeholder={['Start Time', 'End Time']}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Content>
    );
};

export default PerformerSessionsSettings;
